import { useEffect, useRef, useState } from "react";

/**
 * Shared WebSocket subscription. Tries WS first; falls back silently if blocked.
 * Returns { connected, lastMessage, subscribe(handler) } where subscribe returns unsubscribe.
 */
export function useVesselStream() {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const handlersRef = useRef(new Set());
  const stoppedRef = useRef(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + "/api/ws/stream";
    const MAX_ATTEMPTS = 3;

    const connect = () => {
      if (stoppedRef.current || attemptsRef.current >= MAX_ATTEMPTS) return;
      attemptsRef.current += 1;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { attemptsRef.current = 0; setConnected(true); };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            handlersRef.current.forEach((h) => { try { h(msg); } catch {} });
          } catch {}
        };
        ws.onerror = () => {};
        ws.onclose = () => {
          setConnected(false);
          if (stoppedRef.current || attemptsRef.current >= MAX_ATTEMPTS) return;
          setTimeout(connect, 2000 * attemptsRef.current);
        };
      } catch {
        if (!stoppedRef.current && attemptsRef.current < MAX_ATTEMPTS) {
          setTimeout(connect, 2000 * attemptsRef.current);
        }
      }
    };
    connect();
    return () => {
      stoppedRef.current = true;
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  const subscribe = (handler) => {
    handlersRef.current.add(handler);
    return () => handlersRef.current.delete(handler);
  };

  return { connected, subscribe };
}
